import { useEffect, useState } from "react";
import { User, Mail, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Shared Mobile OTP authentication flow (phone -> OTP -> name/email).
// Reused by the Account page and the Checkout auth gate. `onAuthed` fires
// after a fully authenticated + profiled session exists.
export const OtpAuth = ({ onAuthed }) => {
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setInterval(() => setResendIn((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const sendOtp = async () => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) { toast.error("Enter a valid 10-digit mobile number"); return; }
    setBusy(true);
    try {
      const r = await api.sendOtp({ phone: digits });
      toast.success(r.message || "OTP sent");
      if (r.devOtp) toast.message(`Dev OTP: ${r.devOtp}`);
      setStep("otp");
      setResendIn(r.resendIn || 30);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send OTP. Please try again.");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    const code = otp.replace(/\D/g, "");
    if (code.length !== 6) { toast.error("Enter the 6-digit OTP"); return; }
    setBusy(true);
    try {
      const r = await api.verifyOtp({ phone: phone.replace(/\D/g, ""), otp: code });
      if (r.authenticated) {
        if (r.needsProfile) {
          setFirstName(r.customer?.firstName || "");
          setLastName(r.customer?.lastName || "");
          setEmail(r.customer?.email || "");
          setStep("profile");
        } else {
          // Returning customer: recognised by phone, profile already on record.
          // Welcome them back by name instead of any onboarding prompt.
          const name = (r.customer?.firstName || "").trim();
          toast.success(name ? `Welcome back, ${name}!` : "Signed in successfully");
          onAuthed();
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid OTP. Please try again.");
    } finally { setBusy(false); }
  };

  const saveProfile = async () => {
    const name = firstName.trim();
    if (!name) { toast.error("Please enter your name"); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { toast.error("Please enter a valid email address"); return; }
    setBusy(true);
    try {
      await api.completeProfile({ firstName: name, lastName: lastName.trim(), email: email.trim() });
      toast.success("Welcome to AutoCareX!");
      onAuthed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save your details. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-7 w-full max-w-sm" data-testid="otp-auth">
      {step === "phone" && (
        <div className="space-y-3">
          <p className="text-[13px] leading-relaxed text-white/50">
            Sign in with your mobile number. We'll send a secure 6-digit OTP — no password needed.
          </p>
          <div className="flex items-center gap-2 rounded-[12px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] px-3.5 py-3 focus-within:border-[#E11B22]/50">
            <span className="text-[14px] font-medium text-white/50">+91</span>
            <input
              type="tel" inputMode="numeric" autoFocus
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
              onKeyDown={(e) => e.key === "Enter" && sendOtp()}
              placeholder="Mobile number"
              data-testid="otp-phone-input"
              className="w-full bg-transparent text-[15px] text-white outline-none placeholder:text-white/30"
            />
          </div>
          <button onClick={sendOtp} disabled={busy} data-testid="otp-send-button"
            className="inline-flex w-full items-center justify-center gap-2 rounded-[12px] bg-[#E11B22] px-7 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-[#c9151b] disabled:opacity-60">
            {busy && <Loader2 size={16} className="animate-spin" />}
            {busy ? "Sending..." : "Send OTP"}{!busy && <ArrowRight size={16} />}
          </button>
        </div>
      )}

      {step === "otp" && (
        <div className="space-y-3">
          <p className="text-[13px] text-white/50">
            Enter the 6-digit code sent to <span className="font-semibold text-white/80">+91 {phone}</span>
          </p>
          <input
            type="tel" inputMode="numeric" autoFocus maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            onKeyDown={(e) => e.key === "Enter" && verify()}
            placeholder="------"
            data-testid="otp-code-input"
            className="w-full rounded-[12px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] px-3.5 py-3 text-center text-[20px] font-bold tracking-[0.4em] text-white outline-none placeholder:text-white/20 focus:border-[#E11B22]/50"
          />
          <button onClick={verify} disabled={busy} data-testid="otp-verify-button"
            className="inline-flex w-full items-center justify-center gap-2 rounded-[12px] bg-[#E11B22] px-7 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-[#c9151b] disabled:opacity-60">
            {busy && <Loader2 size={16} className="animate-spin" />}
            {busy ? "Verifying..." : "Verify & Continue"}
          </button>
          <div className="flex items-center justify-between text-[13px]">
            <button onClick={() => { setStep("phone"); setOtp(""); }} data-testid="otp-change-number"
              className="text-white/50 transition-colors hover:text-white">
              Change number
            </button>
            <button onClick={sendOtp} disabled={resendIn > 0 || busy} data-testid="otp-resend-button"
              className="font-semibold text-[#E11B22] transition-colors hover:text-[#c9151b] disabled:text-white/30">
              {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend OTP"}
            </button>
          </div>
        </div>
      )}

      {step === "profile" && (
        <div className="space-y-3 text-left" data-testid="otp-profile-step">
          <p className="text-[13px] text-white/50">
            Almost there! Tell us your name and email to finish setting up your account.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 rounded-[12px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] px-3.5 py-3 focus-within:border-[#E11B22]/50">
              <User size={15} className="shrink-0 text-white/40" />
              <input
                type="text" autoFocus value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                data-testid="profile-firstname-input"
                className="w-full bg-transparent text-[15px] text-white outline-none placeholder:text-white/30"
              />
            </div>
            <div className="flex items-center gap-2 rounded-[12px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] px-3.5 py-3 focus-within:border-[#E11B22]/50">
              <input
                type="text" value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
                data-testid="profile-lastname-input"
                className="w-full bg-transparent text-[15px] text-white outline-none placeholder:text-white/30"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-[12px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] px-3.5 py-3 focus-within:border-[#E11B22]/50">
            <Mail size={15} className="shrink-0 text-white/40" />
            <input
              type="email" inputMode="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && saveProfile()}
              placeholder="Email address"
              data-testid="profile-email-input"
              className="w-full bg-transparent text-[15px] text-white outline-none placeholder:text-white/30"
            />
          </div>
          <button onClick={saveProfile} disabled={busy} data-testid="profile-save-button"
            className="inline-flex w-full items-center justify-center gap-2 rounded-[12px] bg-[#E11B22] px-7 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-[#c9151b] disabled:opacity-60">
            {busy && <Loader2 size={16} className="animate-spin" />}
            {busy ? "Saving..." : "Complete sign up"}{!busy && <ArrowRight size={16} />}
          </button>
        </div>
      )}
    </div>
  );
};

export default OtpAuth;
